import type { FC, FormEvent, ReactNode } from 'react'
import type { ServiceArea } from '@/types'

import { useId, useState } from 'react'
import { ArrowRight, CircleCheckBig, MapPin, Navigation } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'
import { findAreaByZip } from '@/constants/areas'
import { QUOTE_PATH } from '@/constants/site'

interface ZipCheckerProps {
    tone?: 'dark' | 'light'
    className?: string
}

type ZipResult = { zip: string, area?: ServiceArea } | null

export const ZipChecker: FC<ZipCheckerProps> = ({ tone = 'dark', className }): ReactNode => {
    const [zip, setZip] = useState('')
    const [result, setResult] = useState<ZipResult>(null)
    const [error, setError] = useState('')
    const inputId = useId()
    const onDark = tone === 'light'

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const value = zip.trim()

        if (!/^\d{5}$/.test(value)) {
            setError("Please enter your 5-digit ZIP code.")
            setResult(null)

            return
        }

        setError('')
        setResult({ zip: value, area: findAreaByZip(value) })
    }

    return (
        <div className={className}>
            <form onSubmit={handleSubmit} noValidate>
                <label htmlFor={inputId} className={cn('block text-sm font-medium', onDark ? 'text-cream' : 'text-ink')}>
                    See if we cover your ZIP
                </label>

                <div
                    className={cn(
                        'mt-3 flex items-center gap-2 rounded-full border p-1.5 pl-5 transition-colors focus-within:border-ember',
                        onDark ? 'border-cream/20 bg-cream/[0.06]' : 'border-ink/15 bg-card'
                    )}
                >
                    <MapPin className={cn('size-4 shrink-0', onDark ? 'text-cream/50' : 'text-slate')} aria-hidden='true' />

                    <input
                        id={inputId}
                        value={zip}
                        onChange={event => setZip(event.target.value.replace(/\D/g, '').slice(0, 5))}
                        inputMode='numeric'
                        autoComplete='postal-code'
                        placeholder="5-digit ZIP"
                        aria-invalid={Boolean(error)}
                        aria-describedby={`${inputId}-result`}
                        className={cn(
                            'h-11 min-w-0 flex-1 bg-transparent font-mono text-[15px] tracking-[0.08em] outline-none',
                            onDark ? 'text-cream placeholder:text-cream/35' : 'text-ink placeholder:text-slate/60'
                        )}
                    />

                    <button
                        type='submit'
                        className='inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-ember px-5 text-[14px] font-medium text-white transition-colors hover:bg-ember-deep'
                    >
                        Look up
                    </button>
                </div>
            </form>

            <div id={`${inputId}-result`} aria-live='polite' className='min-h-6'>
                {error && (
                    <p className={cn('mt-3 text-sm', onDark ? 'text-ember-soft' : 'text-ember')}>{error}</p>
                )}

                {result?.area && (
                    <div
                        className={cn(
                            'mt-4 flex items-start gap-3 rounded-2xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-500',
                            onDark ? 'bg-moss/40 text-cream' : 'bg-moss/10 text-ink'
                        )}
                    >
                        <CircleCheckBig className={cn('mt-0.5 size-5 shrink-0', onDark ? 'text-moss-soft' : 'text-moss')} aria-hidden='true' />

                        <div className='text-[15px] leading-relaxed'>
                            <p className='font-medium'>
                                {result.zip} is in {result.area.name}. You're on our route.
                            </p>

                            <p className={onDark ? 'text-cream/70' : 'text-slate'}>
                                On urgent calls we usually arrive in {result.area.arrival}.
                            </p>

                            <div className='mt-2 flex flex-wrap gap-x-5 gap-y-1'>
                                <TransitionLink href={QUOTE_PATH} className='inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline'>
                                    Request your written price
                                    <ArrowRight className='size-3.5' aria-hidden='true' />
                                </TransitionLink>

                                <TransitionLink href={`/service-areas/${result.area.slug}`} className='inline-flex items-center gap-1 underline-offset-4 hover:underline'>
                                    Our work in {result.area.name}
                                </TransitionLink>
                            </div>
                        </div>
                    </div>
                )}

                {result && !result.area && (
                    <div
                        className={cn(
                            'mt-4 flex items-start gap-3 rounded-2xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-500',
                            onDark ? 'bg-cream/[0.07] text-cream' : 'bg-sand text-ink'
                        )}
                    >
                        <Navigation className='mt-0.5 size-5 shrink-0 text-amber' aria-hidden='true' />

                        <div className='text-[15px] leading-relaxed'>
                            <p className='font-medium'>
                                {result.zip} falls outside our 8 towns.
                            </p>

                            <p className={onDark ? 'text-cream/70' : 'text-slate'}>
                                Send the request anyway. We'll let you know quickly whether we can help.
                            </p>

                            <TransitionLink href={QUOTE_PATH} className='mt-2 inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline'>
                                Send a request
                                <ArrowRight className='size-3.5' aria-hidden='true' />
                            </TransitionLink>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
